import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { chemicalsAPI } from '../services/api';
import recentSearchesManager from '../utils/recentSearches';
import RecentSearchesSidebar from '../components/RecentSearchesSidebar';
import SEO from '../components/SEO';

function Chemicals() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchSource, setSearchSource] = useState('both'); // both, chebi, chembl
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const resultsPerPage = 10;

  // Auto-search if query parameter is present
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && query.trim()) {
      setSearchQuery(query);
      performSearch(query, searchSource, 1);
    }
  }, [searchParams, searchSource]);

  const performSearch = async (query, source, page = 1) => {
    setLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * resultsPerPage;
      const response = await chemicalsAPI.search(query, source, resultsPerPage, skip);
      setResults(response.data);
      setCurrentPage(page);

      // Calculate total pages based on results
      const totalResults = (response.data?.chebi?.results?.length || 0) + (response.data?.chembl?.results?.length || 0);
      setTotalPages(totalResults < resultsPerPage ? page : page + 1);

      // Save to recent searches (only on first page)
      if (page === 1) {
        recentSearchesManager.addSearch({
          query: query.trim(),
          database: 'chemicals',
          searchType: source,
          resultsCount: totalResults
        });
      }

    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // Update URL with search query
    setSearchParams({ q: searchQuery.trim() });

    await performSearch(searchQuery.trim(), searchSource, 1);
  };

  const handlePageChange = async (page) => {
    if (page < 1 || page > totalPages) return;
    await performSearch(searchQuery.trim(), searchSource, page);
  };

  const renderChEBIResults = () => {
    if (!results?.chebi) {
      return null;
    }

    if (results.chebi.error) {
      return (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ color: '#e74c3c' }}>ChEBI Service</h3>
          <p style={{ color: '#95a5a6' }}>⚠️ {results.chebi.error}</p>
        </div>
      );
    }

    if (!results.chebi.results || results.chebi.results.length === 0) {
      return null;
    }

    return (
      <div>
        <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>ChEBI Results</h3>
        <div className="results-grid">
          {results.chebi.results.map((chemical, index) => (
            <div key={index} className="result-card">
              <h3>{chemical.chebiAsciiName || 'Unknown'}</h3>
              <p><strong>ChEBI ID:</strong> {chemical.chebiId || 'N/A'}</p>
              <p><strong>Search Score:</strong> {chemical.searchScore || 'N/A'}</p>
              <p><strong>Star Rating:</strong> {chemical.entityStar || 'N/A'}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderChEMBLResults = () => {
    if (!results?.chembl) {
      return null;
    }

    if (results.chembl.error) {
      return (
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ color: '#e74c3c' }}>ChEMBL Service</h3>
          <p style={{ color: '#95a5a6' }}>⚠️ {results.chembl.error}</p>
        </div>
      );
    }

    if (!results.chembl.molecules || results.chembl.molecules.length === 0) {
      return null;
    }

    return (
      <div>
        <h3 style={{ marginTop: '2rem', marginBottom: '1rem', color: '#2c3e50' }}>ChEMBL Results</h3>
        <div className="results-grid">
          {results.chembl.molecules.map((molecule, index) => (
            <Link to={`/chemical/${molecule.molecule_chembl_id}`} key={index} className="clickable-card" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="result-card hoverable-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <h3 style={{ margin: 0 }}>{molecule.pref_name || 'Unknown'}</h3>
                  <span style={{ 
                    backgroundColor: '#11998e', 
                    color: 'white', 
                    padding: '0.25rem 0.5rem', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>ChEMBL</span>
                </div>
              
              <div style={{ marginTop: '1rem' }}>
                <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Identifiers</h4>
                <p><strong>ChEMBL ID:</strong> <span style={{ color: '#3498db', fontFamily: 'monospace' }}>{molecule.molecule_chembl_id || 'N/A'}</span></p>
                <p><strong>Type:</strong> {molecule.molecule_type || 'N/A'}</p>
                {molecule.first_approval && (
                  <p><strong>First Approval:</strong> {molecule.first_approval}</p>
                )}
              </div>

              {molecule.max_phase !== null && molecule.max_phase !== undefined && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Development Status</h4>
                  <p><strong>Max Clinical Phase:</strong> <span style={{ 
                    color: molecule.max_phase === '4.0' || molecule.max_phase === 4 ? '#27ae60' : 
                          molecule.max_phase === '3.0' || molecule.max_phase === 3 ? '#3498db' : 
                          molecule.max_phase === '2.0' || molecule.max_phase === 2 ? '#f39c12' : '#95a5a6',
                    fontWeight: 'bold'
                  }}>Phase {molecule.max_phase}</span></p>
                  {molecule.oral !== undefined && (
                    <p><strong>Oral:</strong> {molecule.oral ? '✓ Yes' : '✗ No'}</p>
                  )}
                  {molecule.parenteral !== undefined && (
                    <p><strong>Parenteral:</strong> {molecule.parenteral ? '✓ Yes' : '✗ No'}</p>
                  )}
                  {molecule.topical !== undefined && (
                    <p><strong>Topical:</strong> {molecule.topical ? '✓ Yes' : '✗ No'}</p>
                  )}
                </div>
              )}

              {molecule.molecule_properties && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Molecular Properties</h4>
                  {molecule.molecule_properties.full_molformula && (
                    <p><strong>Formula:</strong> {molecule.molecule_properties.full_molformula}</p>
                  )}
                  {molecule.molecule_properties.full_mwt && (
                    <p><strong>Molecular Weight:</strong> {molecule.molecule_properties.full_mwt}</p>
                  )}
                  {molecule.molecule_properties.alogp !== undefined && (
                    <p><strong>AlogP:</strong> {molecule.molecule_properties.alogp}</p>
                  )}
                  {molecule.molecule_properties.hba !== undefined && (
                    <p><strong>H-Bond Acceptors:</strong> {molecule.molecule_properties.hba}</p>
                  )}
                  {molecule.molecule_properties.hbd !== undefined && (
                    <p><strong>H-Bond Donors:</strong> {molecule.molecule_properties.hbd}</p>
                  )}
                  {molecule.molecule_properties.psa && (
                    <p><strong>Polar Surface Area:</strong> {molecule.molecule_properties.psa} Ų</p>
                  )}
                  {molecule.molecule_properties.rtb !== undefined && (
                    <p><strong>Rotatable Bonds:</strong> {molecule.molecule_properties.rtb}</p>
                  )}
                  {molecule.molecule_properties.aromatic_rings !== undefined && (
                    <p><strong>Aromatic Rings:</strong> {molecule.molecule_properties.aromatic_rings}</p>
                  )}
                </div>
              )}

              {molecule.atc_classifications && molecule.atc_classifications.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>ATC Classifications</h4>
                  <p>{molecule.atc_classifications.join(', ')}</p>
                </div>
              )}

              {molecule.molecule_structures?.canonical_smiles && (
                <div style={{ marginTop: '1rem' }}>
                  <h4 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>Chemical Structure</h4>
                  <p style={{ 
                    fontSize: '0.85em', 
                    fontFamily: 'monospace', 
                    backgroundColor: '#f8f9fa', 
                    padding: '0.5rem', 
                    borderRadius: '4px',
                    overflowX: 'auto',
                    wordBreak: 'break-all'
                  }}>
                    <strong>SMILES:</strong> {molecule.molecule_structures.canonical_smiles.substring(0, 100)}
                    {molecule.molecule_structures.canonical_smiles.length > 100 ? '...' : ''}
                  </p>
                  {molecule.molecule_structures.standard_inchi_key && (
                    <p style={{ 
                      fontSize: '0.85em', 
                      fontFamily: 'monospace', 
                      backgroundColor: '#f8f9fa', 
                      padding: '0.5rem', 
                      borderRadius: '4px',
                      marginTop: '0.5rem'
                    }}>
                      <strong>InChI Key:</strong> {molecule.molecule_structures.standard_inchi_key}
                    </p>
                  )}
                </div>
              )}

              {molecule.black_box_warning === 1 && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: '#ffe6e6', 
                  borderRadius: '4px',
                  borderLeft: '4px solid #e74c3c'
                }}>
                  <strong style={{ color: '#c0392b' }}>⚠️ BLACK BOX WARNING</strong>
                </div>
              )}

              {molecule.withdrawn_flag && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: '#fff3cd', 
                  borderRadius: '4px',
                  borderLeft: '4px solid #ffc107'
                }}>
                  <strong style={{ color: '#856404' }}>⚠️ WITHDRAWN FROM MARKET</strong>
                </div>
              )}
              
              <div className="view-details-link">
                View Full Details →
              </div>
            </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  // SEO structured data
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Chemical Data Search',
    'description': 'Search chemical entities from ChEBI and ChEMBL databases',
    'specialty': 'Chemical Informatics'
  };

  return (
    <div className="page-container">
      <SEO
        title="Chemical Data Search - ChEBI & ChEMBL Database"
        description="Search chemical entities from ChEBI (Chemical Entities of Biological Interest) and ChEMBL (bioactive molecules with drug-like properties) databases. Find chemical compounds, structures, and bioactivity data."
        keywords="chemical database, ChEBI, ChEMBL, chemical entities, bioactive molecules, chemical compounds, molecular structures, drug discovery"
        canonical="/chemicals"
        structuredData={seoStructuredData}
      />
      <h1 className="page-title">Chemical Data Search</h1>
      <p className="page-description">
        Search chemical entities from ChEBI (Chemical Entities of Biological Interest) and 
        ChEMBL (bioactive molecules with drug-like properties) databases.
      </p>

      <div className="search-section">
        <form onSubmit={handleSearch}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="both"
                checked={searchSource === 'both'}
                onChange={(e) => setSearchSource(e.target.value)}
              />
              {' '}Search Both
            </label>
            <label style={{ marginRight: '1rem' }}>
              <input
                type="radio"
                value="chebi"
                checked={searchSource === 'chebi'}
                onChange={(e) => setSearchSource(e.target.value)}
              />
              {' '}ChEBI Only
            </label>
            <label>
              <input
                type="radio"
                value="chembl"
                checked={searchSource === 'chembl'}
                onChange={(e) => setSearchSource(e.target.value)}
              />
              {' '}ChEMBL Only
            </label>
          </div>

          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Enter chemical name (e.g., caffeine, aspirin)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="search-button" disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
        </form>
      </div>

      {error && <div className="error">Error: {error}</div>}
      
      {loading && <div className="loading">Loading...</div>}

      {!loading && results && (
        <div className="results-section">
          <div className="results-header">
            <h2>Results</h2>
            {totalPages > 1 && (
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
            )}
          </div>

          {renderChEBIResults()}
          {renderChEMBLResults()}

          {(!results.chebi?.results?.length && !results.chembl?.molecules?.length) && (
            <p className="no-results">No results found</p>
          )}

          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="pagination-btn"
              >
                ← Previous
              </button>

              <div className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  if (pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`pagination-btn ${pageNum === currentPage ? 'active' : ''}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="pagination-btn"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      <RecentSearchesSidebar database="chemicals" />
    </div>
  );
}

export default Chemicals;
